/**
 * EFFECT_REGISTRY — the registry that turns an EffectLayer (data) into live
 * sprites (a handle you can destroy). The AtmosphereLayer is a thin loop over
 * a scene's layers: look up the builder by `kind`, build it with the shared
 * BuildCtx, collect the handles; tear them all down on unmount.
 *
 * Each effect kind registers ONE descriptor from its own module
 * (effects/<kind>Effect.ts) — adding a new effect is: write the module, call
 * registerEffect({ kind, build, depth?, mooded? }). No edits to the loop.
 */
import type { Region, Bounds } from './atmospherePlacement';
import type { EffectLayer, EffectKind } from '../../../config/atmosphere/effectLayer.types';
import type { ISprite, WorldLayerHandle } from '../../rendering/IRenderer';

/** Live camera viewport in world coords (PixiOverlayHost.getCameraView). */
export interface CameraView {
  centerX: number;
  centerY: number;
  width: number;
  height: number;
}

/** Everything a builder needs, shared across all layers of one mount. */
export interface BuildCtx {
  /** Camera-anchored scatter region (falls back to the level box). */
  region: Region;
  /** The level's world box. */
  bounds: Bounds;
  /** Deterministic seed (hashSeed(levelId)) so placement is stable across reloads. */
  seed: number;
  levelId: string;
  /** Resolved per-level scene (terrain anchors + biome + mood + regions). The
   *  TERRAIN-fixed basis: SCENERY (parallax/sky/floor-glow) anchors to scene.*;
   *  AMBIENT motes use `region` (camera-fixed). Keep the two bases distinct. */
  scene: import('./scene/resolveScene').ResolvedScene;
  /** Subscribe to the overlay ticker; returns an unsubscribe (PixiOverlayHost.addTickListener). */
  addTick: (fn: (dtMs: number) => void) => () => void;
  /** Live camera view in world coords (PixiOverlayHost.getCameraView). */
  getCameraView: () => CameraView | null;
  /** Live screen size in logical pixels (PixiOverlayHost.getScreenSize). */
  getScreenSize?: () => { width: number; height: number } | null;
  /** Per-layer display container (Scene FX v3 M1). Builders mint sprites/graphics
   *  into it (via SpriteOptions.parent / GraphicsStyle.parent). Opacity is owned by
   *  AtmosphereLayer (container.alpha) — builders never touch it. undefined only
   *  when the host wasn't ready (degraded → worldContainer). */
  layerContainer?: WorldLayerHandle;
}

/** A built effect's lifecycle handle — destroy() removes ALL its sprites + tick listeners. */
export interface EffectHandle {
  destroy(): void;
  /** Debug (VQA) — how many ambient sprites this effect contributes. */
  spriteCount?(): number;
  /** Debug (VQA) — soft-light count (lightField only; for `__atmosphere.softLightCount`). */
  lightCount?(): number;
  // ── Modulation hooks (Scene Effects v2) — the AtmosphereLayer calls these
  //    each frame from a layer's modulators. A handle implements only the ones
  //    it can honour; unimplemented targets are simply not driven. `mult` is a
  //    multiplier on the layer's authored baseline (1 = as authored). Opacity is
  //    NOT here — it's a container concern composed centrally by AtmosphereLayer
  //    (container.alpha), reaching every kind uniformly (Scene FX v3 M1). ──
  /** Scale emitter spawn density (particle streams) — surges/lulls. */
  setDensity?(mult: number): void;
  /** Scale emitter travel speed (particle streams). */
  setSpeed?(mult: number): void;
  /** Shift the layer's colour: `t` (0..1) lerps each sprite from its authored
   *  base colour toward the layer's `tintTo`. Only driven when the layer has BOTH
   *  a `tint` modulator and a `tintTo`; absent driver = the authored colour. */
  setTint?(t: number): void;
  /** Update a colorGrade layer's ramp colours + per-strip strength live (phase 2:
   *  the TimeOfDayDriver pushes a time-keyed palette each tick so night reads
   *  cool-dark). The strip stack redraws on the next camera-fit tick. Only
   *  honoured by the `colorGrade` kind; absent on other handles. */
  setGrade?(top: number, bottom: number, strength: number): void;
  /** React to a world-space reactivity pulse (impact/kill/… — the positional
   *  channel of the world-combat reactivity bus). Called once per accepted
   *  pulse (not per frame). Transient only — the handle must return to its
   *  authored behavior on its own (fireflies scatter then drift home, critters
   *  flee then settle back to lane speed). `intensity` is the pulse intensity ×
   *  the response amp; `radius` is world px. Opt-in per kind like setDensity. */
  onPulse?(x: number, y: number, intensity: number, radius: number): void;
}

/** Build one layer into a handle. Return null to contribute nothing (e.g. texture not ready). */
export type EffectBuilder = (layer: EffectLayer, ctx: BuildCtx) => EffectHandle | null;

/** A self-contained effect kind: how to build it, where its container sits in the
 *  z-stack, and whether scene-mood dims it. Co-located with the builder module so
 *  adding a kind is one self-registering file — no edits to AtmosphereLayer. */
export interface EffectDescriptor {
  kind: EffectKind;
  build: EffectBuilder;
  /** Container zIndex in worldContainer. Default Z_LAYERS.ATMOSPHERE (applied by
   *  AtmosphereLayer). parallax → BACKGROUND, vignette → 950. */
  depth?: number;
  /** Whether the scene-mood dial scales this layer's opacity. Ambient beds → true;
   *  frame kinds (vignette/parallax) → false. Default true. */
  mooded?: boolean;
}

export const EFFECT_REGISTRY: Partial<Record<EffectKind, EffectDescriptor>> = {};

/** Register a self-contained effect kind (once per module at load). */
export function registerEffect(desc: EffectDescriptor): void {
  EFFECT_REGISTRY[desc.kind] = desc;
}

/** Lifecycle handle for an effect's CONTENT (sprites/ticks/emitter). Opacity is NOT
 *  here — it's a container concern owned by AtmosphereLayer. destroy() runs onDestroy
 *  (tick unsub / emitter.destroy) then destroys owned sprites. setDensity/setSpeed
 *  pass emitter content-modulation through; lightCount/spriteCount are VQA probes. */
export function makeContentHandle(opts: {
  sprites?: ISprite[];
  onDestroy?: () => void;
  /** Override the spriteCount probe (for kinds whose count isn't the sprite
   *  array length — e.g. leviathans counts whales, not the IGraphics objects). */
  spriteCount?: () => number;
  lightCount?: () => number;
  setDensity?: (mult: number) => void;
  setSpeed?: (mult: number) => void;
  setTint?: (t: number) => void;
  setGrade?: (top: number, bottom: number, strength: number) => void;
  onPulse?: (x: number, y: number, intensity: number, radius: number) => void;
}): EffectHandle {
  const { sprites = [], onDestroy, spriteCount: customSpriteCount, lightCount, setDensity, setSpeed, setTint, setGrade, onPulse } = opts;
  const handle: EffectHandle = {
    destroy: () => {
      try { onDestroy?.(); } catch { /* gone */ }
      for (const s of sprites) { try { s.destroy(); } catch { /* gone */ } }
    },
  };
  if (customSpriteCount) handle.spriteCount = customSpriteCount;
  else if (sprites.length) handle.spriteCount = () => sprites.length;
  if (lightCount) handle.lightCount = lightCount;
  if (setDensity) handle.setDensity = setDensity;
  if (setSpeed) handle.setSpeed = setSpeed;
  if (setTint) handle.setTint = setTint;
  if (setGrade) handle.setGrade = setGrade;
  if (onPulse) handle.onPulse = onPulse;
  return handle;
}
